import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Badge } from '@/components/ui/badge';
import { Star, Info, X, Sparkles, ChevronRight, ExternalLink } from 'lucide-react';
import { DignityAvatar } from '@/components/scenario-builder/DignityAvatar';
import { useBrand, type BrandKey } from '@/config/brands';

interface Palette {
  navy: string;
  navyMid: string;
  red: string;
  gold: string;
  goldDeep: string;
  goldSoft: string;
  cream: string;
  parchment: string;
  emerald: string;
}

/**
 * Per-brand palette factory. Field semantics are preserved across brands so
 * the modal's component tree doesn't need to know which brand is active:
 *   navy/navyMid — primary dark surface
 *   red          — alert/cornerstone marker
 *   gold/goldDeep — accent / "Dignity explains" header
 *   cream/parchment — modal background + light text
 *   emerald      — semantic positive marker
 *
 * AHP stays patriotic. Luigi's reads as cyberpunk-dark — green/cyan accents on
 * black surfaces — so the modal stops looking like an Independence Day pamphlet
 * inside a Matrix-themed site. AntiGreed gets cream-on-deep-green.
 */
function buildPalette(brandKey: BrandKey): Palette {
  if (brandKey === 'luigis') {
    return {
      navy: '#0a0a0a',
      navyMid: '#141414',
      red: '#ff0033',
      gold: '#00ffff',
      goldDeep: '#00b3b3',
      goldSoft: 'rgba(0,255,255,0.12)',
      cream: '#e0ffe0',
      parchment: '#0a0a0a',
      emerald: '#00ff66',
    };
  }
  if (brandKey === 'antigreed') {
    return {
      navy: '#0a1408',
      navyMid: '#0f1d12',
      red: '#ff6600',
      gold: '#e8d4b0',
      goldDeep: '#b8a380',
      goldSoft: 'rgba(232,212,176,0.14)',
      cream: '#f5e6d3',
      parchment: '#0a1408',
      emerald: '#7ddc8f',
    };
  }
  return {
    navy: '#001238',
    navyMid: '#002868',
    red: '#B22234',
    gold: '#C5A55A',
    goldDeep: '#9A7B33',
    goldSoft: '#F5EBD7',
    cream: '#FAF9F6',
    parchment: '#FBF7EE',
    emerald: '#10B981',
  };
}

function usePalette(): Palette {
  const brand = useBrand();
  return useMemo(() => buildPalette(brand.key), [brand.key]);
}

interface ScenarioProcedureRow {
  id: string;
  code: string;
  code_type: string;
  description: string | null;
  phase: string | null;
  setting: string | null;
  quantity: number | null;
  is_required: boolean | null;
  cornerstone_notes: string | null;
  canonical_unit: string | null;
  quantity_audit_ok: boolean | null;
  quantity_audit_notes: string | null;
}

interface ScenarioHowItWorksModalProps {
  scenarioId: string;
  scenarioName: string;
  scenarioDescription?: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

// ─── DIGNITY VN MESSAGE PANEL ──────────────────────────────────────────
// Sticks at the top of the modal. Two-line script that reads as a quick
// in-character explanation of the methodology before the user dives into
// the code list. Scripted; no LLM round-trip needed for the modal open.
const DIGNITY_LINES: Array<{ heading: string; body: string }> = [
  {
    heading: "Here's how I read this scenario.",
    body: "Each procedure has a billing code, a quantity, and a national-average price. I multiply each one out, sum them, and that's the scenario total. The codes marked with a star are cornerstones — without them, a hospital can't credibly say they offer this treatment.",
  },
  {
    heading: "Mean vs. median matters here.",
    body: "If you flip the toggle on the card, you'll see the median total — the middle hospital, half charge less and half charge more. The mean (average) gets pulled up by a few hospitals charging extreme amounts. The median is what most people would actually be billed.",
  },
  {
    heading: "Hospitals get ranked only if they qualify.",
    body: "A hospital needs three out of four scenario codes in their chargemaster — and ALL of the cornerstone codes — before I'll rank them. Otherwise comparing them is apples to oranges. That's why specialty facilities sometimes show fewer scenarios than general hospitals.",
  },
];

function DignityIntro() {
  const PALETTE = usePalette();
  const [lineIdx, setLineIdx] = useState(0);
  const line = DIGNITY_LINES[lineIdx];
  const isLast = lineIdx === DIGNITY_LINES.length - 1;

  return (
    <div
      className="rounded-lg p-4 sm:p-5 mb-5 relative overflow-hidden"
      style={{
        background: `linear-gradient(135deg, ${PALETTE.navy} 0%, ${PALETTE.navyMid} 100%)`,
        border: `1px solid ${PALETTE.gold}55`,
        boxShadow: `0 0 0 1px ${PALETTE.gold}22, 0 8px 24px -10px rgba(0,18,56,0.4)`,
      }}
    >
      {/* Flag-stripe top accent — same patriotic device as Quality cards */}
      <div
        className="absolute top-0 left-0 right-0 h-1"
        style={{ background: `linear-gradient(90deg, ${PALETTE.red} 0%, ${PALETTE.cream} 50%, ${PALETTE.gold} 100%)` }}
        aria-hidden
      />
      <div className="flex items-start gap-4 pt-1">
        <DignityAvatar size="chat" isSpeaking={true} />
        <div className="flex-1 min-w-0">
          <div className="text-[11px] uppercase tracking-widest font-semibold mb-1" style={{ color: PALETTE.gold }}>
            Dignity explains
          </div>
          <h4
            className="text-base sm:text-lg font-semibold leading-tight mb-1.5"
            style={{ fontFamily: "'Instrument Serif', Georgia, serif", fontWeight: 400, color: PALETTE.cream }}
          >
            {line.heading}
          </h4>
          <p className="text-sm leading-relaxed" style={{ color: 'rgba(245,235,215,0.85)' }}>
            {line.body}
          </p>
          <div className="flex items-center gap-2 mt-3">
            <div className="flex gap-1">
              {DIGNITY_LINES.map((_, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => setLineIdx(i)}
                  aria-label={`Step ${i + 1} of ${DIGNITY_LINES.length}`}
                  className="w-1.5 h-1.5 rounded-full transition-all"
                  style={{
                    background: i === lineIdx ? PALETTE.gold : 'rgba(245,235,215,0.25)',
                    transform: i === lineIdx ? 'scale(1.5)' : 'scale(1)',
                  }}
                />
              ))}
            </div>
            <span className="flex-1" />
            <button
              type="button"
              onClick={() => setLineIdx(isLast ? 0 : lineIdx + 1)}
              className="text-xs px-2 py-1 rounded inline-flex items-center gap-1 transition-colors"
              style={{
                color: PALETTE.gold,
                border: `1px solid ${PALETTE.gold}55`,
                background: 'rgba(197,165,90,0.08)',
              }}
            >
              {isLast ? 'Replay' : 'Next'} <ChevronRight className="w-3 h-3" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── PER-CODE TOOLTIP CONTENT ──────────────────────────────────────────
// Shows the cornerstone reasoning + quantity audit result + canonical unit,
// pulled from the cornerstone audit pass.
function CodeTooltip({ proc }: { proc: ScenarioProcedureRow }) {
  return (
    <div className="text-xs space-y-1.5 max-w-[280px]">
      {proc.is_required ? (
        <div className="font-semibold text-amber-300 flex items-center gap-1">
          <Star className="w-3 h-3 fill-current" /> Cornerstone code
        </div>
      ) : (
        <div className="font-semibold opacity-75">Supportive code</div>
      )}
      {proc.cornerstone_notes && <div className="opacity-90">{proc.cornerstone_notes}</div>}
      {proc.canonical_unit && (
        <div className="opacity-75 text-[10px]">
          Canonical billing unit: <span className="font-mono font-semibold">{proc.canonical_unit}</span>
        </div>
      )}
      {proc.quantity_audit_ok === false && proc.quantity_audit_notes && (
        <div className="text-[10px] text-red-300 border-t border-red-300/30 pt-1.5">
          ⚠ Quantity audit flagged: {proc.quantity_audit_notes}
        </div>
      )}
    </div>
  );
}

// ─── MAIN MODAL ──────────────────────────────────────────────────────────
export function ScenarioHowItWorksModal({
  scenarioId,
  scenarioName,
  scenarioDescription,
  open,
  onOpenChange,
}: ScenarioHowItWorksModalProps) {
  const PALETTE = usePalette();
  const { data: procedures, isLoading } = useQuery({
    queryKey: ['scenario-procedures-for-modal', scenarioId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('scenario_procedures')
        .select('id, code, code_type, description, phase, setting, quantity, is_required, cornerstone_notes, canonical_unit, quantity_audit_ok, quantity_audit_notes')
        .eq('scenario_id', scenarioId)
        .order('sort_order', { ascending: true });
      if (error) throw error;
      return (data ?? []) as ScenarioProcedureRow[];
    },
    enabled: open,
  });

  const grouped = useMemo(() => {
    const m = new Map<string, ScenarioProcedureRow[]>();
    for (const p of procedures ?? []) {
      const ph = p.phase || 'Procedures';
      if (!m.has(ph)) m.set(ph, []);
      m.get(ph)!.push(p);
    }
    return Array.from(m.entries());
  }, [procedures]);

  const cornerstoneCount = procedures?.filter(p => p.is_required).length ?? 0;
  const totalCount = procedures?.length ?? 0;
  const auditedCount = procedures?.filter(p => p.cornerstone_notes != null).length ?? 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-3xl max-h-[90vh] overflow-y-auto p-0"
        style={{ background: PALETTE.parchment }}
      >
        {/* Patriotic top stripe */}
        <div
          className="h-1 sticky top-0 z-10"
          style={{ background: `linear-gradient(90deg, ${PALETTE.red} 0%, ${PALETTE.cream} 50%, ${PALETTE.navyMid} 100%)` }}
        />
        <div className="p-5 sm:p-6">
          {/* HEADER */}
          <div className="flex items-start justify-between gap-3 mb-4">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <Sparkles className="w-3.5 h-3.5" style={{ color: PALETTE.gold }} />
                <span className="text-[11px] uppercase tracking-widest font-semibold" style={{ color: PALETTE.goldDeep }}>
                  How this scenario is calculated
                </span>
              </div>
              <DialogTitle asChild>
                <h2
                  className="text-2xl sm:text-3xl font-semibold leading-tight"
                  style={{ fontFamily: "'Instrument Serif', Georgia, serif", fontWeight: 400, color: PALETTE.navy }}
                >
                  {scenarioName}
                </h2>
              </DialogTitle>
              {scenarioDescription && (
                <p className="text-sm mt-1.5 max-w-prose" style={{ color: 'rgba(0,18,56,0.65)' }}>
                  {scenarioDescription}
                </p>
              )}
            </div>
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              className="p-2 rounded hover:bg-black/5 transition-colors flex-shrink-0"
              aria-label="Close"
              style={{ color: 'rgba(0,18,56,0.55)' }}
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* DIGNITY INTRO PANEL */}
          <DignityIntro />

          {/* QUICK STATS */}
          <div className="grid grid-cols-3 gap-3 mb-5">
            <StatTile label="Total codes" value={totalCount.toString()} accent={PALETTE.navy} />
            <StatTile label="Cornerstones" value={cornerstoneCount.toString()} accent={PALETTE.red} icon={<Star className="w-3 h-3 fill-current" />} />
            <StatTile label="Audited" value={`${auditedCount}/${totalCount}`} accent={PALETTE.emerald} />
          </div>

          {/* CODE BREAKDOWN BY PHASE */}
          <h3
            className="text-base font-semibold mb-3 mt-1"
            style={{ fontFamily: "'Instrument Serif', Georgia, serif", fontWeight: 400, color: PALETTE.navy }}
          >
            Procedure codes used
          </h3>

          {isLoading ? (
            <div className="text-sm text-center py-8" style={{ color: 'rgba(0,18,56,0.55)' }}>Loading codes…</div>
          ) : (
            <TooltipProvider delayDuration={200}>
              <div className="space-y-4">
                {grouped.map(([phase, items]) => (
                  <section key={phase}>
                    <h4 className="text-[11px] uppercase tracking-widest font-semibold mb-2" style={{ color: PALETTE.goldDeep }}>
                      {phase}
                    </h4>
                    <div className="space-y-1.5">
                      {items.map(proc => (
                        <CodeRow key={proc.id} proc={proc} />
                      ))}
                    </div>
                  </section>
                ))}
              </div>
            </TooltipProvider>
          )}

          {/* FOOTER LINK */}
          <div className="mt-6 pt-4 border-t flex items-center justify-between" style={{ borderColor: 'rgba(0,18,56,0.10)' }}>
            <p className="text-xs" style={{ color: 'rgba(0,18,56,0.55)' }}>
              Methodology evolves. Read the full methodology for current rules and update history.
            </p>
            <Link
              to="/data-methodology"
              className="text-xs font-semibold inline-flex items-center gap-1 hover:underline"
              style={{ color: PALETTE.red }}
            >
              Full methodology <ExternalLink className="w-3 h-3" />
            </Link>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ─── HELPER: STAT TILE ───────────────────────────────────────────────────
function StatTile({ label, value, accent, icon }: { label: string; value: string; accent: string; icon?: React.ReactNode }) {
  // Standalone helper — no access to the parent's usePalette() value.
  const PALETTE = usePalette();
  return (
    <div
      className="relative rounded-md p-3 pl-4"
      style={{
        background: '#FFFFFF',
        border: `1px solid rgba(0,18,56,0.10)`,
        boxShadow: '0 1px 0 rgba(255,255,255,0.6) inset, 0 2px 6px -3px rgba(0,18,56,0.08)',
      }}
    >
      <div
        className="absolute top-2 bottom-2 left-0 w-[2.5px] rounded-full"
        style={{ background: accent, boxShadow: `0 0 6px ${accent}55` }}
        aria-hidden
      />
      <div className="text-[10px] uppercase tracking-wider mb-0.5 flex items-center gap-1" style={{ color: 'rgba(0,18,56,0.55)' }}>
        {icon}{label}
      </div>
      <div
        className="text-xl font-semibold tabular-nums"
        style={{ fontFamily: "'Instrument Serif', Georgia, serif", fontWeight: 400, color: PALETTE.navy }}
      >
        {value}
      </div>
    </div>
  );
}

// ─── HELPER: CODE ROW ────────────────────────────────────────────────────
function CodeRow({ proc }: { proc: ScenarioProcedureRow }) {
  const PALETTE = usePalette();
  const isCornerstone = proc.is_required === true;
  const codeTypeRoute =
    proc.code_type === 'CPT' ? `/cpt/${proc.code}`
    : proc.code_type === 'HCPCS' ? `/hcpcs/${proc.code}`
    : proc.code_type === 'DRG' || proc.code_type === 'MS-DRG' ? `/drg/${proc.code}`
    : proc.code_type === 'REV' ? `/rev/${proc.code}`
    : null;

  return (
    <div
      className="relative rounded-md p-2.5 pl-4 flex items-start gap-3 transition-shadow hover:shadow-sm"
      style={{
        background: isCornerstone ? 'rgba(178,34,52,0.04)' : '#FFFFFF',
        border: `1px solid ${isCornerstone ? 'rgba(178,34,52,0.25)' : 'rgba(0,18,56,0.08)'}`,
      }}
    >
      {/* Flag-stripe — red for cornerstone, neutral for supportive */}
      <div
        className="absolute top-2 bottom-2 left-0 w-[2.5px] rounded-full"
        style={{ background: isCornerstone ? PALETTE.red : 'rgba(0,18,56,0.18)' }}
        aria-hidden
      />
      <div className="flex-shrink-0">
        {isCornerstone ? (
          <Star className="w-4 h-4 fill-current" style={{ color: PALETTE.red }} />
        ) : (
          <div className="w-4 h-4 rounded-full border" style={{ borderColor: 'rgba(0,18,56,0.18)' }} />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline gap-2 flex-wrap">
          {codeTypeRoute ? (
            <Link
              to={codeTypeRoute}
              className="font-mono font-semibold text-sm hover:underline"
              style={{ color: isCornerstone ? PALETTE.red : PALETTE.navy }}
            >
              {proc.code}
            </Link>
          ) : (
            <span className="font-mono font-semibold text-sm" style={{ color: isCornerstone ? PALETTE.red : PALETTE.navy }}>
              {proc.code}
            </span>
          )}
          <Badge variant="outline" className="text-[9px] py-0 px-1" style={{ borderColor: 'rgba(0,18,56,0.20)', color: 'rgba(0,18,56,0.65)' }}>
            {proc.code_type}
          </Badge>
          {proc.quantity != null && proc.quantity > 1 && (
            <span className="text-[10px] tabular-nums font-mono" style={{ color: 'rgba(0,18,56,0.55)' }}>
              ×{proc.quantity}
            </span>
          )}
          {proc.canonical_unit && (
            <span className="text-[10px] font-mono" style={{ color: 'rgba(0,18,56,0.40)' }}>
              ({proc.canonical_unit})
            </span>
          )}
          {proc.quantity_audit_ok === false && (
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded font-semibold" style={{ background: 'rgba(178,34,52,0.12)', color: PALETTE.red, border: `1px solid rgba(178,34,52,0.35)` }}>
                  qty review
                </span>
              </TooltipTrigger>
              <TooltipContent>
                <CodeTooltip proc={proc} />
              </TooltipContent>
            </Tooltip>
          )}
        </div>
        {proc.description && (
          <p className="text-xs mt-0.5 line-clamp-2" style={{ color: 'rgba(0,18,56,0.65)' }}>
            {proc.description}
          </p>
        )}
      </div>
      {(proc.cornerstone_notes || proc.canonical_unit) && (
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              className="flex-shrink-0 p-1 rounded hover:bg-black/5 transition-colors"
              aria-label="Why this code"
              style={{ color: 'rgba(0,18,56,0.40)' }}
            >
              <Info className="w-3.5 h-3.5" />
            </button>
          </TooltipTrigger>
          <TooltipContent>
            <CodeTooltip proc={proc} />
          </TooltipContent>
        </Tooltip>
      )}
    </div>
  );
}
